import {HttpClient} from '@angular/common/http'
import {computed, inject, Injectable, signal} from '@angular/core'
import {MatDialog} from '@angular/material/dialog'
import {BehaviorSubject, tap} from 'rxjs'
import {environment} from '../../environments/environment'
import {ICustomer} from '../application/types'

@Injectable({
  providedIn: 'root'
})
export class CustomerService {

  public customer = signal<ICustomer>({} as ICustomer)
  public customers = new BehaviorSubject<ICustomer[]>([])

  /**
   * This is only used when we open a dialog with a customer if we get a
   * 409 conflict from the BE when we try to create a customer that already
   * exists
   */
  public currentCreatingCustomerId = ''

  public validForSign = computed(() => {
    let valid = !!this.customer().mobile || !!this.customer().email
    if (this.customer().isOrganization) {
      valid = !!this.customer().mobile || !!this.customer().email && !!this.customer().companySignatoryPersonalNumber
    }
    return valid
  })

  private readonly http = inject(HttpClient)
  private readonly dialog = inject(MatDialog)

  public getCustomers() {
    const url = `${environment.irApiUrl}/customers`
    return this.http.get<ICustomer[]>(url).pipe(
      tap(customers => this.customers.next(customers))
    )
  }

  public getCustomer(id: string) {
    const url = `${environment.irApiUrl}/customers/${id}`
    return this.http.get<ICustomer>(url).pipe(
      tap(customer => this.customer.set(customer))
    )
  }

  public createCustomer(customer: ICustomer) {
    this.currentCreatingCustomerId = customer.sub
    const url = `${environment.irApiUrl}/customers`
    return this.http.post<ICustomer>(url, customer)
  }

  public updateCustomer(customer: ICustomer) {
    const url = `${environment.irApiUrl}/customers/${customer.id}`
    return this.http.put<ICustomer>(url, customer)
  }

  /**
   * Delete an existing customer.
   * @param id - The customer ID
   */
  public wipeCustomer(id: string) {
    const url = `${environment.irApiUrl}/customers/${id}/wipe`
    return this.http.delete<void>(url).pipe(
      tap(() => this.customers.next(this.customers.value.filter(c => c.id !== id)))
    )
  }
}
